/** @format */

import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { AbstractControl, FormControl, FormGroup, Validators } from "@angular/forms";
import { ToastService } from "mis-crystal-design-system/toast";
import { Moment, parseZone, tz } from "moment";
import { combineLatest, Observable, of, Subject, Subscription, throwError } from "rxjs";
import { catchError, debounceTime, map, take, takeUntil } from "rxjs/operators";
import { CONSTANTS_ASSETS_EMP_WEB_EXP } from "../../../constants/assets.constants";
import { NsWISConfigurations } from "../../../models/configurations.model";
import { NsEmpWebExpData } from "../../../models/emp-web-exp.model";
import { ApiService } from "../../../services/api.service";
import { BookingDataService } from "../../../services/booking-data.service";
import { CommuteService } from "../../../services/commute.service";
import { createBookingRequest, editBookingRequest } from "./booking-generators";
import { SeatService } from "../../../services/seat.service";
import { PreferenceDataService } from "../../../services/preference-data.service";
import { MealService } from "../../../modules/meal-module/meal.service";
import { getAdditionalBookings, labelForShifts } from "../../../functions/booking-utils.functions";
import { ConfigurationService } from "../../../services/configuration.service";
import { AdhocShiftsComponent } from "../../modals/adhoc-shifts/adhoc-shifts.component";
import { ModalService } from "mis-design-system/mis-modal";
import { IDatePickerConfig, IDatePickerToastText } from "mis-crystal-design-system/datepicker_v2";
import { RecurrenceConfirmationComponent } from "../../modals/recurrence-confirmation/recurrence-confirmation.component";

const parkingTypes = {
  PARKING_TWO: "TWO-WHEELER-PARKING",
  PARKING_FOUR: "FOUR-WHEELER-PARKING"
};

const seatAddon: NsEmpWebExpData.IAddon = {
  id: "SEAT",
  name: "Add Desk",
  icon: CONSTANTS_ASSETS_EMP_WEB_EXP.ICONS.DESK_BULE,
  iconBgColor: "#F2FFEF"
};

const commuteAddon: NsEmpWebExpData.IAddon = {
  id: "COMMUTE",
  name: "Add Cab/Parking",
  icon: CONSTANTS_ASSETS_EMP_WEB_EXP.ICONS.CAB,
  iconBgColor: "#FDF5FD"
};

const mealAddon: NsEmpWebExpData.IAddon = {
  id: "MEAL",
  name: "Add Meals",
  icon: CONSTANTS_ASSETS_EMP_WEB_EXP.ICONS.MEAL,
  iconBgColor: "#F2FFEF"
};

@Component({
  selector: "mis-employee-web-exp-booking-form",
  templateUrl: "./booking-form.component.html",
  styleUrls: ["./booking-form.component.scss"]
})
export class BookingFormComponent implements OnInit, OnDestroy {
  Assets = CONSTANTS_ASSETS_EMP_WEB_EXP;
  loginShiftList: NsEmpWebExpData.IShiftData[] = [];
  logoutShiftList: NsEmpWebExpData.IShiftData[] = [];
  nextDaylogoutShiftList: NsEmpWebExpData.IShiftData[] = [];
  offices: NsEmpWebExpData.IOffice[] = [];
  preferenceData: NsEmpWebExpData.IPreference;
  mealMapValue: NsEmpWebExpData.IMealsEntity[] = [];
  meal: NsEmpWebExpData.IMeal[] = [];
  private endObs = new Subject<void>();
  bookingForm: FormGroup;
  prefOfc: NsEmpWebExpData.IOffice;
  dateFormat: string = "DD/MM/YYYY";
  startDatepickerConfig: Partial<IDatePickerConfig> = {
    minDate: parseZone().tz(this.bookingSvc.timezone).format(this.dateFormat),
    format: this.dateFormat,
    maxDate: parseZone().tz(this.bookingSvc.timezone).format(this.dateFormat),
    timezone: this.bookingSvc.timezone
  };
  endDatepickerConfig: Partial<IDatePickerConfig> = {
    minDate: parseZone().tz(this.bookingSvc.timezone).format(this.dateFormat),
    format: this.dateFormat,
    maxDate: parseZone().tz(this.bookingSvc.timezone).format(this.dateFormat),
    timezone: this.bookingSvc.timezone
  };
  formReset = new Subject<void>();
  formHeading = "Create Booking";
  loader = false;
  parkingOptions: NsEmpWebExpData.IPremise[] = [];
  availableAddons: NsEmpWebExpData.IAddon[] = [];
  addonConfigurations: NsEmpWebExpData.IAddon;
  addonDataSubscription: Subscription;
  maxDuration: number = 0;
  bookingReasons = [] || "";
  disableOfficeChange = false;
  @Input() formConfigs: NsEmpWebExpData.IBookingFormConfig;
  @Output() create = new EventEmitter<[NsEmpWebExpData.IBookingRequest[] | NsEmpWebExpData.IBookingRequestObject, boolean]>(true);
  @Output() update = new EventEmitter<NsEmpWebExpData.IBookingRequest[]>(true);
  @Output() close = new EventEmitter<void>(true);
  @Output() expand = new EventEmitter<{ addon: NsEmpWebExpData.IAddon; shiftValid: boolean }>(true);
  configurations: NsWISConfigurations.IConfigurations;
  drawerExpanded = false;
  datesDisabled: string[] = [];
  weeklyOffs: number[] = [];
  datepickerMessages: IDatePickerToastText[] = [];
  adhocEnabled = false;
  checkoutList = [];
  startTimeInMinutes: number;
  recurrenceBookingEnabled = false;
  mealCutOff: boolean;
  constructor(
    public bookingSvc: BookingDataService,
    private preferenceSvc: PreferenceDataService,
    private apiService: ApiService,
    private toasts: ToastService,
    public configSvc: ConfigurationService,
    private commuteSvc: CommuteService,
    private seatSvc: SeatService,
    private mealService: MealService,
    private modals: ModalService
  ) {}

  ngOnInit(): void {
    this.dateFormat = this.formConfigs.userConfigs.flattenedSettings.DATE_FORMAT;
    this.formHeading = this.formConfigs.heading;
    this.seatSvc.setSeatMapSource(this.formConfigs.userConfigs?.flattenedSettings);
    const configs = this.formConfigs.userConfigs as NsWISConfigurations.IConfigurations;
    const bookingFeatures = configs.BUSettings.features.find(f => f.id === "booking");
    bookingFeatures?.seatBookingEnabled && this.availableAddons.push(seatAddon);
    (configs.flattenedSettings.showCabs || configs.flattenedSettings.showParking) && this.availableAddons.push(commuteAddon);
    const mealEnabled = configs.flattenedSettings.mealPlanningEnabled;
    const adhocCheck = configs.BUSettings.features.find(item => item.id === "schedule");
    this.adhocEnabled = adhocCheck?.adhocEnabled;
    if (mealEnabled) this.availableAddons.push(mealAddon);
    this.formHeading = this.formConfigs.heading;
    this.recurrenceBookingEnabled = this.formConfigs.userConfigs.flattenedSettings.recurrenceBookingEnabled;
    if (this.formConfigs.type === "WFH" || configs.flattenedSettings.approvalFlowEnabled) {
      this.setBookingReasons();
    }
    combineLatest([
      this.formConfigs.mode === "CREATE"
        ? this.preferenceSvc.todayPreferences.pipe(
            catchError(err => {
              this.toasts.displayMsg("Unable to fetch preferences.", 4000);
              return throwError("Preference fetch error. Err:", err);
            })
          )
        : of({}),
      this.bookingSvc.offices.pipe(
        catchError(err => {
          this.toasts.displayMsg("Unable to fetch offices!", 4000);
          return throwError("Offices fetch error. Err:", err);
        })
      )
    ])
      .pipe(take(1))
      .subscribe(([preference, offices]) => {
        this.preferenceData = preference;
        this.offices = offices.map(o => ({
          ...o,
          label: o.address,
          value: o.guid
        }));
        let booking = this.formConfigs.data;
        if (this.formConfigs.mode === "CREATE" && this.formConfigs.nearbyBookingDetails) {
          booking = {
            ...booking,
            officeId: this.formConfigs.nearbyBookingDetails?.officeGuid,
            startTime: this.formConfigs.nearbyBookingDetails?.startTime,
            endTime: this.formConfigs.nearbyBookingDetails?.endTime
          };
        }
        this.bookingForm = this.buildForm(booking);
        this.setFormFieldStates(this.bookingForm);
        this.setChangelisteners(this.bookingForm);
        this.checkAndGetShifts(this.bookingForm.get("startDate"), this.bookingForm.get("endDate"));
        const configs = this.formConfigs.userConfigs as NsWISConfigurations.IConfigurations;
        this.dateFormat = configs.flattenedSettings.DATE_FORMAT;
        const date = parseZone()
          .tz(this.bookingSvc.timezone)
          .set("hours", 0)
          .set("minutes", 0)
          .set("seconds", 0)
          .set("milliseconds", 0)
          .format(this.dateFormat);
        this.startDatepickerConfig.format = this.dateFormat;
        this.endDatepickerConfig.format = this.dateFormat;
        this.startDatepickerConfig.minDate = date;
        this.endDatepickerConfig.minDate = date;
        this.bookingSvc.profileRules.pipe(takeUntil(this.endObs)).subscribe(res => {
          this.maxDuration = res.bulkScheduleAllowedDays;
          const maxMoment = parseZone().add(this.maxDuration, "days").tz(this.bookingSvc.timezone);
          this.startDatepickerConfig.maxDate = maxMoment.format(this.dateFormat);
          this.endDatepickerConfig.maxDate = maxMoment.format(this.dateFormat);
          this.setDatepickerDates(maxMoment);
          if (this.bookingSvc.wfoExpiryTime) {
            this.startDatepickerConfig.maxDate = this.bookingSvc.wfoExpiryTime.format(this.dateFormat);
            this.endDatepickerConfig.maxDate = this.startDatepickerConfig.maxDate;
          }
        });
      });
    this.seatSvc.officeChange.pipe(takeUntil(this.endObs)).subscribe(res => {
      const ofc = this.offices.find(o => o.guid === res.officeGuid);
      this.onOfficeChange(ofc, true);
      this.bookingForm.patchValue({ seat: res });
    });
  }

  handleRecurrenceData(data: FormGroup) {
    let { recurrenceVisibility, endDate, repeatsEvery } = data.value;
    if (repeatsEvery.days) {
      repeatsEvery = {
        ...repeatsEvery,
        days: repeatsEvery.days.map(item => {
          if (item === 7) item = 1;
          else item = item + 1;
          return item;
        })
      };
    }
    this.bookingForm.controls.recurrence.patchValue({ recurrenceVisibility, endDate, repeatsEvery });
    (this.bookingForm.controls.recurrence as FormGroup).controls = data.controls;
  }

  openAdhoc(data: string) {
    const initialState = {
      ignoreBackdropClick: true,
      class: "exp-adhoc-modal",
      configs: this.formConfigs.userConfigs,
      data: data,
      shifts: [this.loginShiftList, this.logoutShiftList],
      nextDayLogoutShift: this.nextDaylogoutShiftList,
      formData: this.bookingForm,
      adhocEnabled: this.adhocEnabled
    };
    const ref = this.modals.show(AdhocShiftsComponent, { ...initialState, initialState });
    ref.afterClosed.subscribe((res: any) => {
      if (res) {
        this.onShiftChange(res.shiftType.toLowerCase(), res);
      }
    });
  }

  handleRecurrenceEvent() {
    this.bookingForm.get("recurrence").value.recurrenceVisibility = !this.bookingForm.get("recurrence").value.recurrenceVisibility;
  }

  private setDatepickerDates(maxMoment: Moment): void {
    const tempDate = parseZone().tz(this.bookingSvc.timezone).startOf("day");
    this.apiService
      .getAllowedDates(tempDate.format("YYYY-MM-DD"), maxMoment.format("YYYY-MM-DD"))
      .pipe(map(dates => dates.map(date => parseZone(date).tz(this.bookingSvc.timezone))))
      .subscribe(dates => {
        while (tempDate.isSameOrBefore(maxMoment)) {
          const date = dates.find(d => d.isSame(tempDate, "day"));
          const isEmpWeeklyOffIncludes = this.formConfigs.profile.empWeeklyOff.includes(tempDate.day() + 1);
          if (isEmpWeeklyOffIncludes || !date) {
            const dateWithFormat = tempDate.format(this.dateFormat);
            this.datesDisabled.push(dateWithFormat);
            this.datepickerMessages.push({
              date: dateWithFormat,
              message: isEmpWeeklyOffIncludes ? "Weekly Off" : "Not allowed due to company policies"
            });
          } else if (this.bookingSvc.wfoExpiryTime && tempDate.isAfter(this.bookingSvc.wfoExpiryTime.clone().startOf("day"))) {
            this.datepickerMessages.push({
              date: tempDate.format(this.dateFormat),
              message: "You are not allowed to book on this date. Please reach out to the admin for more details."
            });
          }
          tempDate.add(1, "day");
        }
      });
  }

  ngOnDestroy(): void {
    this.endObs.next();
    this.endObs.complete();
    this.addonDataSubscription?.unsubscribe();
    this.formReset.next();
    this.formReset.complete();
  }

  closeForm() {
    this.close.emit();
    this.mealService.bookingResponse(null);
    this.drawerExpanded = false;
  }

  private buildForm(booking?: NsEmpWebExpData.IBookingDetailDTOS) {
    let office;
    let seat: NsEmpWebExpData.ISeatData;
    let meal: NsEmpWebExpData.IMeal[] = [];
    const ofc = this.offices.find(ofc => ofc.guid === (this.preferenceData.officeGuid || this.formConfigs.profile.officeGuid));
    if (this.formConfigs.mode === "CREATE") {
      if (ofc) {
        office = ofc;
      }
      if (this.preferenceData.seatDetails) {
        seat = this.preferenceData.seatDetails;
      }
    }

    let startDate: string;
    let endDate: string;
    const today = parseZone().tz(this.bookingSvc.timezone);
    if (!this.formConfigs.profile.empWeeklyOff.includes(today.day() + 1) && !this.datesDisabled.find(d => parseZone(d).isSame(today, "day"))) {
      startDate = today.clone().format(this.dateFormat);
      endDate = today.clone().format(this.dateFormat);
    }
    if (booking) {
      const ofc = this.offices.find(ofc => ofc.guid === booking?.officeId);
      office = ofc || office;
      startDate = parseZone(booking.startTime)
        .tz(booking.timezone || office.timezone || this.bookingSvc.timezone)
        .format(this.dateFormat);

      endDate = parseZone(booking.endTime)
        .tz(booking.timezone || office.timezone || this.bookingSvc.timezone)
        .format(this.dateFormat);

      if (booking.tokenId && booking.tokenName && this.formConfigs.type === "OFFICE") {
        seat = {
          seatId: booking.tokenId,
          seatName: booking.tokenName,
          floorId: booking.premiseId,
          floorName: booking.premiseName
        };
      }
      let mapMeal = new Map<string, NsEmpWebExpData.IMealsEntity>();
      const mealDataFromBooking = getAdditionalBookings(booking, ["MEALS"])?.[0];
      const mealAdditionalBooking = mealDataFromBooking && JSON.parse(JSON.stringify(mealDataFromBooking));
      mealAdditionalBooking?.extras.mealData?.forEach(element => {
        mapMeal.set(element.mealId, element);
      });
      if (mealAdditionalBooking?.extras?.mealData) {
        mealAdditionalBooking.extras.mealData = [{ ...(mealAdditionalBooking.extras as NsEmpWebExpData.IMealsEntity) }];
        mealAdditionalBooking.extras.mealMap = mapMeal;
        meal.push(mealAdditionalBooking.extras as NsEmpWebExpData.IMeal);
      }
      if (meal?.[0]?.mealData?.[0]?.["mealData"]) meal[0].mealData = [...meal?.[0]?.mealData?.[0]?.["mealData"]];
    }
    if (seat?.seatId) {
      this.seatSvc.changeSeat(seat);
    }
    const form = new FormGroup({
      bookingRequestType: new FormControl(this.formConfigs.type || "OFFICE", [Validators.required]),
      office: new FormControl(office, [Validators.required]),
      startDate: new FormControl(startDate, this.formConfigs.mode === "CREATE" ? [Validators.required] : []),
      endDate: new FormControl(endDate, this.formConfigs.mode === "CREATE" ? [Validators.required] : []),
      startShift: new FormControl("", [Validators.required]),
      endShift: new FormControl("", [Validators.required]),
      premiseId: new FormControl("", [Validators.required]),
      commute: new FormGroup({
        login: new FormControl(undefined, []),
        logout: new FormControl(undefined, []),
        registrationNumber: new FormControl("", [])
      }),
      seat: new FormControl(
        seat?.seatId ? seat : null,
        this.formConfigs.userConfigs.flattenedSettings.seatMandatory && this.formConfigs.type === "OFFICE" ? [Validators.required] : []
      ),
      meal: new FormControl(meal.length ? meal : undefined, []),
      reason: new FormControl(booking?.bookingReason || "", this.formConfigs.type === "WFH" ? [Validators.required] : []),
      recurrence: new FormGroup({
        recurrenceVisibility: new FormControl(false),
        endDate: new FormControl(endDate || undefined, []),
        repeatsEvery: new FormGroup({
          days: new FormControl([], []),
          repeatedDay: new FormControl()
        })
      })
    });
    return form;
  }

  getControl(name: string): AbstractControl {
    return this.bookingForm.get(name);
  }

  private setChangelisteners(form: FormGroup): void {
    this.formReset.next();
    const startDateCtrl = form.get("startDate");
    const endDateCtrl = form.get("endDate");
    const startShiftCtrl = form.get("startShift");
    const endShiftCtrl = form.get("endShift");
    const office = form.get("office");
    office.valueChanges.pipe(debounceTime(10), takeUntil(this.formReset)).subscribe(office => {
      this.checkAndGetShifts(startDateCtrl, endDateCtrl, false, false, false);
    });
    startDateCtrl.valueChanges.pipe(debounceTime(10), takeUntil(this.formReset)).subscribe((start: string) => {
      this.bookingForm.get("recurrence").value.recurrenceVisibility = false;
      this.bookingForm.patchValue({ startShift: undefined, endShift: undefined, meal: undefined });
      this.endDatepickerConfig.minDate = start;
      endDateCtrl.patchValue(start, { emitEvent: false, onlySelf: false });
      if (!this.bookingSvc.formConfiguration.userConfigs.flattenedSettings.restrictBookingSingleDay)
        endDateCtrl.enable({ emitEvent: false, onlySelf: false });
      this.preferenceSvc.refreshReplayStreams({
        startTime: parseZone(start, this.dateFormat).startOf("day").valueOf().toString(),
        officeGuid: office.value.guid
      });
      this.checkAndGetShifts(startDateCtrl, endDateCtrl, false, true);
    });
    endDateCtrl.valueChanges.pipe(debounceTime(10), takeUntil(this.formReset)).subscribe(end => {
      this.bookingForm.patchValue({ startShift: undefined, endShift: undefined, meal: undefined });
      this.checkAndGetShifts(startDateCtrl, endDateCtrl);
    });
    startShiftCtrl.valueChanges.pipe(takeUntil(this.formReset)).subscribe(shift => endShiftCtrl.enable());
    combineLatest([startShiftCtrl.valueChanges, endShiftCtrl.valueChanges])
      .pipe(debounceTime(10), takeUntil(this.formReset))
      .subscribe(([startShift, endShift]) => {
        this.bookingForm.patchValue({ meal: undefined });
        this.mealService.changedMealData([]);
        if (this.addonConfigurations) {
          this.updateAddon(this.addonConfigurations);
        }
      });
  }

  private setFormFieldStates(form: FormGroup): void {
    const startDate = form.get("startDate");
    const endDate = form.get("endDate");
    const startShift = form.get("startShift");
    const endShift = form.get("endShift");
    const { restrictBookingSingleDay } = this.formConfigs.userConfigs.flattenedSettings;
    // if(restrictBookingSingleDay) {
    //   endDate.disable();
    // }
    if (this.formConfigs.mode === "CREATE") {
      startDate.valid && !restrictBookingSingleDay ? endDate.enable() : endDate.disable();
      startDate.valid && (endDate.valid || endDate.disabled) ? startShift.enable() : startShift.disable();
      startDate.valid && (endDate.valid || endDate.disabled) && startShift.valid ? endShift.enable() : endShift.disable();
    } else {
      startDate.disable();
      endDate.disable();
      const { data: booking, userConfigs: configs } = this.formConfigs;
      const bookingStart = parseZone(booking.startTime);
      const bookingEnd = parseZone(booking.endTime);
      const now = parseZone();
      if (
        bookingStart.subtract(Number(configs.flattenedSettings.employeeScheduleCutoff), "hours").isAfter(now) &&
        booking.requestStatus !== "SIGNED_IN"
      ) {
        startShift.enable();
      } else {
        startShift.disable();
        startShift.clearValidators();
        startShift.updateValueAndValidity();
        this.startTimeInMinutes = parseZone(booking.startTime).diff(parseZone(booking.startTime).startOf("day")) / (1000 * 60);
      }

      if (
        bookingEnd.subtract(Number(configs.flattenedSettings.employeeEndTimeBookingCutoff), "hours").isAfter(now) &&
        booking.requestStatus !== "SIGNED_OUT"
      ) {
        endShift.enable();
      } else {
        endShift.disable();
        endShift.clearValidators();
        endShift.updateValueAndValidity();
      }
    }
    const cfg = this.formConfigs.userConfigs as NsWISConfigurations.IConfigurations;
    const schCfg = cfg.BUSettings.features.find(f => f.id === "schedule");
    if (schCfg.showEmployeeProfileOfficeOnly) {
      this.disableOfficeChange = true;
    }
  }

  onOfficeChange(office: NsEmpWebExpData.IOffice, external = false): void {
    this.bookingForm.patchValue({ meal: [], startShift: undefined, endShift: undefined });
    this.bookingForm.get("commute").patchValue({ login: undefined, logout: undefined, registrationNumber: undefined });
    this.seatSvc.mapChangedOffice = external;
    this.bookingForm.patchValue({ office, seat: undefined }, { emitEvent: true, onlySelf: true });
    this.setdatepickerConfigs(office);
    // if (this.availableAddons.includes(seatAddon) && this.formConfigs.mode === "CREATE") {
    //   this.loader = true;
    //   const currentDate = new Date();
    //   const firstday = new Date(currentDate.setDate(currentDate.getDate() - currentDate.getDay() + 1)).toUTCString();
    //   this.apiService.getUpdatedSeatPreference(new Date(firstday).getTime(), office.value).subscribe((seat) => {
    //     this.bookingForm.patchValue({ seat });
    //   });
    // }
  }

  expandAddon(addon: NsEmpWebExpData.IAddon): void {
    if (this.mealCutOff && addon.id === "MEAL") {
      this.showCutOffMsg();
    } else {
      this.addonConfigurations = addon;
      this.updateAddon(addon);
      this.expand.emit({
        addon,
        shiftValid: this.bookingForm.get("startShift").valid && this.bookingForm.get("endShift").valid
      });
      this.drawerExpanded = true;
    }
  }

  async updateAddon(addon: NsEmpWebExpData.IAddon) {
    this.addonConfigurations = addon;
    this.addonDataSubscription?.unsubscribe();
    const { startShift, endShift, startDate, endDate, office, seat, meal } = this.bookingForm.value || {};
    const ofc = this.offices.find(o => o.guid === office.value);
    let startTime = tz(startDate, this.dateFormat, ofc.timezone).startOf("day");
    if (this.formConfigs.mode === "EDIT") {
      const bkng = this.formConfigs.data as NsEmpWebExpData.IBookingDetailDTOS;
      startTime = parseZone(bkng.startTime)
        .tz(bkng.timezone || ofc.timezone)
        .startOf("day");
    }
    if (addon.id === "COMMUTE") {
      this.loader = true;
      await this.commuteSvc.setFormData(
        this.formConfigs.profile,
        this.formConfigs.userConfigs,
        this.bookingForm.value,
        startTime.valueOf(),
        ofc.timezone,
        this.bookingForm.get("commute")?.value
      );
      this.loader = false;
      this.addonDataSubscription = this.commuteSvc.commuteChange.pipe(takeUntil(this.endObs)).subscribe(res => {
        this.handleCommuteChange(res);
      });
    } else if (addon.id === "SEAT") {
      const start = startShift ? startTime.valueOf() + startShift?.timeInMinutes * 60 * 1000 : -1;
      const end = endShift ? startTime.valueOf() + endShift?.timeInMinutes * 60 * 1000 : -1;
      this.seatSvc.openMap(office, start, end, seat, this.formConfigs.nearbyBookingDetails);
      this.addonDataSubscription = this.seatSvc.seatChange.subscribe(res => {
        if (res?.seatId) {
          this.bookingForm.patchValue({ meal: undefined });
          this.bookingForm.patchValue({ seat: res });
        }
      });
    } else if (addon.id === "MEAL") {
      const start = startShift ? startTime.valueOf() + startShift?.timeInMinutes * 60 * 1000 : -1;
      const end = endShift ? startTime.valueOf() - 1 + endShift?.timeInMinutes * 60 * 1000 : -1;
      this.seatSvc.seatChange.subscribe(res => {
        if (res) this.mealService.startEndShiftData(start, end, ofc.timezone, res?.floorId);
        else this.mealService.startEndShiftData(start, end, ofc.timezone);
      });
      this.mealService.changedGuid(office.value);
      this.mealService.bookingResponse(meal ? [...meal] : undefined);
      this.addonDataSubscription = this.mealService.mealChange.subscribe(res => {
        this.handleMealChange(res);
      });
    }
  }

  async checkAndGetShifts(
    start?: AbstractControl,
    end?: AbstractControl,
    officeChange?: boolean,
    updatePreferences: boolean = false,
    updateShifts: boolean = true
  ) {
    start = start || this.bookingForm.get("startDate");
    end = end || this.bookingForm.get("endDate");
    this.setMealCutoffInMinutes(start, end);
    if (start.invalid) {
      this.bookingForm?.get("startShift").disable();
      this.bookingForm?.get("endShift").disable();
    }
    end.invalid && this.bookingForm?.get("endShift").disable();
    if ((start.valid && (end.valid || end.disabled)) || this.formConfigs.mode === "EDIT") {
      this.loader = true;
      if (!this.bookingForm.get("startDate").disabled && updatePreferences) {
        this.preferenceData = await this.preferenceSvc.otherDayPreference.pipe(take(1)).toPromise();
      }
      this.bookingSvc.getShifts(start, end, this.bookingForm.value.office).subscribe(([login, logout, nextDay, premises]) => {
        [this.loginShiftList, this.logoutShiftList, this.nextDaylogoutShiftList] = [login, logout, nextDay];
        this.checkoutList = [
          ...this.logoutShiftList,
          ...(this.nextDaylogoutShiftList.length > 0 && [{ label: "Next Day", value: "Next Day", type: "LEGEND" }, ...this.nextDaylogoutShiftList])
        ];
        // this.bookingForm?.get("startShift").enable();
        // this.bookingForm?.get("endShift").enable();
        this.setParkingAndPremises(premises as NsEmpWebExpData.IPremise[]);
        officeChange ? this.bookingForm.patchValue({ startShift: undefined, endShift: undefined }) : updateShifts && this.setShifts();
        !officeChange && this.setCommute();
        this.loader = false;
        const seatAddon = this.availableAddons.find(addon => addon.id === "SEAT");
        if (
          this.formConfigs.mode === "CREATE" &&
          !!this.formConfigs.nearbyBookingDetails?.teamMateEmpGuid &&
          seatAddon &&
          (!this.addonConfigurations || this.addonConfigurations?.id === "SEAT")
        ) {
          this.expandAddon(seatAddon);
        }
      });
    }
  }

  setdatepickerConfigs(office: NsEmpWebExpData.IOffice): void {
    const today = parseZone().tz(office.timezone);
    const minDate = today.format(this.dateFormat);
    const maxDate = today.clone().add(this.maxDuration, "days");
    this.bookingSvc.timezone = office.timezone;
    this.startDatepickerConfig = {
      ...this.startDatepickerConfig,
      minDate,
      maxDate:
        this.bookingSvc.wfoExpiryTime && maxDate.isAfter(this.bookingSvc.wfoExpiryTime)
          ? this.bookingSvc.wfoExpiryTime.format(this.dateFormat)
          : maxDate.format(this.dateFormat),
      timezone: office.timezone
    };
    this.endDatepickerConfig = {
      ...this.endDatepickerConfig,
      minDate,
      maxDate:
        this.bookingSvc.wfoExpiryTime && maxDate.isAfter(this.bookingSvc.wfoExpiryTime)
          ? this.bookingSvc.wfoExpiryTime.format(this.dateFormat)
          : maxDate.format(this.dateFormat),
      timezone: office.timezone
    };
    this.bookingForm.patchValue({ startDate: minDate, endDate: minDate }, { emitEvent: false, onlySelf: false });
  }

  setMealCutoffInMinutes(start, end) {
    const timeZone = this.bookingForm.value.office.timezone;
    const mealCutoffInMinutes = this.formConfigs.userConfigs.flattenedSettings["mealCutoffInMinutes"];
    const setCutOff = -mealCutoffInMinutes;
    const selectedDate = parseZone(start.value).tz(timeZone).startOf("day").valueOf();
    const todaysDate = parseZone().tz(timeZone).valueOf();
    const enableTillTime = selectedDate + setCutOff * 1000 * 60;
    this.mealCutOff = !(todaysDate < enableTillTime);
  }
  showCutOffMsg() {
    this.toasts.displayMsg("The latest time upto which you can book meals is already passed.");
  }

  private setParkingAndPremises(premises: NsEmpWebExpData.IPremise[]): void {
    if (!premises?.length) {
      this.toasts.displayMsg("No premises found for the office!");
      return;
    }
    const premise = premises.find(premise => premise.premiseTypeText === "BUILDING")?.premiseId;
    if (premise) {
      this.bookingForm.patchValue({ premiseId: premise });
    }
  }

  private setShifts(): void {
    if (this.formConfigs.mode === "EDIT") {
      const booking = this.formConfigs.data as NsEmpWebExpData.IBookingDetailDTOS;
      const format = this.formConfigs.userConfigs.flattenedSettings.TIME_FORMAT;
      let startShift: { label: string; shiftNotFound: boolean } | NsEmpWebExpData.IShiftData = this.loginShiftList.find(
        s => s.shiftId === booking.loginShiftId
      );
      if (!startShift) {
        startShift = labelForShifts(booking.startTime, booking.timezone, format);
        this.bookingForm.get("startShift").disable();
      }
      let endShift: { label: string; shiftNotFound: boolean } | NsEmpWebExpData.IShiftData = booking?.extras?.isNextDayLogout
        ? this.nextDaylogoutShiftList.find(s => s.shiftId === booking.logoutShiftId)
        : this.logoutShiftList.find(s => s.shiftId === booking.logoutShiftId);
      if (!endShift) {
        endShift = labelForShifts(booking.endTime, booking.timezone, format);
        this.bookingForm.get("endShift").disable();
      }
      this.bookingForm.patchValue({ startShift, endShift }, { onlySelf: true, emitEvent: false });
      this.formConfigs.data.requestStatus === "SIGNED_IN" && this.bookingForm.get("startShift").disable();
    } else {
      const startShift = this.loginShiftList.find(
        s =>
          s.shiftId ===
          (this.formConfigs.nearbyBookingDetails?.loginShiftId ||
            this.preferenceData?.defaultLoginShift?.scheduleEventId ||
            this.preferenceData?.loginShiftId)
      );
      const endShift = this.logoutShiftList.find(
        s =>
          s.shiftId ===
          (this.formConfigs.nearbyBookingDetails?.logoutShiftId ||
            this.preferenceData?.defaultLogoutShift?.scheduleEventId ||
            this.preferenceData?.logoutShiftId)
      );
      this.bookingForm.get("startShift").enable();
      this.bookingForm.get("endShift").enable();
      if (startShift && endShift) {
        this.bookingForm.patchValue({ startShift, endShift }, { onlySelf: true, emitEvent: false });
      } else {
        this.bookingForm.patchValue({ seat: undefined });
      }
    }
  }

  private setCommute(): void {
    const booking = this.formConfigs.data as NsEmpWebExpData.IBookingDetailDTOS;
    let loginCommute;
    let logoutCommute;
    let regNo;
    if (this.formConfigs.mode === "CREATE") {
      loginCommute = this.preferenceData.loginTransport
        ? {
            id: "transport",
            disable: false,
            name: "Office Cab"
          }
        : undefined;
      logoutCommute = this.preferenceData.logoutTransport
        ? {
            id: "transport",
            disable: false,
            name: "Office Cab"
          }
        : undefined;
    } else {
      if (booking?.extras?.login || booking?.extras?.logout) {
        loginCommute = booking?.extras?.login
          ? {
              id: "transport",
              disable: false,
              name: "Office Cab"
            }
          : undefined;
        logoutCommute = booking?.extras?.logout
          ? {
              id: "transport",
              disable: false,
              name: "Office Cab"
            }
          : undefined;
      } else if (booking.bookingRequestType === "OFFICE") {
        const parking = getAdditionalBookings(booking, Object.keys(parkingTypes))?.[0];
        // console.log(parking);
        if (parking) {
          loginCommute = {
            id: "parking",
            disable: false,
            value: { premiseId: parking.premiseId, premise: parking.premiseName, seatId: parking?.tokenId, seatName: parking?.tokenName }
          };
          regNo = parking?.extras?.registrationNumber;
        }
      }
    }
    this.bookingForm.get("commute").patchValue({ login: loginCommute, logout: logoutCommute, registrationNumber: regNo });
  }

  onShiftChange(type: string | "login" | "logout", shift: NsEmpWebExpData.IShiftData) {
    if (type === "login") {
      this.bookingForm.get("startShift").patchValue(shift);
      const end = this.bookingForm.get("endShift");
      if (end.value?.timeInMinutes <= shift?.timeInMinutes) {
        this.bookingForm.get("endShift").reset();
      }
    } else if (type === "logout") {
      this.bookingForm.get("endShift").patchValue(shift);
    }
    const commute = this.bookingForm.get("commute");
    if (commute?.value?.[type]?.id === "transport" && !shift?.transportAvalability) {
      commute.patchValue({ [type]: undefined });
    }
    this.updateSeat();
  }

  private updateSeat(): void {
    if (!this.availableAddons.find(a => a.id === "SEAT") || this.formConfigs.mode !== "CREATE") return;
    const startDate = this.bookingForm.get("startDate").value;
    const endDate = this.bookingForm.get("endDate").value;
    const seatInfo = this.bookingForm.get("seat").value;
    const officeInfo = this.bookingForm.get("office").value;
    const startShift = this.bookingForm.get("startShift").value;
    const endShift = this.bookingForm.get("endShift").value;
    const start = tz(startDate, this.dateFormat, officeInfo.timezone).add(startShift?.timeInMinutes, "minutes");
    if (startDate && endDate && this.bookingForm.get("bookingRequestType").value === "OFFICE") {
      // if seat is selected, check availability, else fetch preference availability based on selected shift timings
      if (seatInfo && seatInfo?.seatId) {
        this.apiService
          .getSeatAvailablity({
            seatId: seatInfo.seatId,
            startTime: parseZone(startDate, this.dateFormat)
              .tz(this.bookingSvc.timezone)
              .set("hour", startShift?.hours)
              .set("minute", startShift?.minutes)
              .set("second", 0)
              .valueOf(),
            endTime: parseZone(endDate, this.dateFormat)
              .tz(this.bookingSvc.timezone)
              .set("hour", endShift?.hours)
              .set("minute", endShift?.minutes)
              .set("second", 0)
              .valueOf(),
            officeGuid: officeInfo.guid
          })
          .subscribe(res => {
            if (res?.errorCode === 200) return;
            if (res?.message) {
              this.toasts.displayMsg(res.message, 4000);
              this.bookingForm.patchValue({ seat: undefined });
            }
          });
      } else if (startShift && endShift) {
        this.preferenceSvc.refreshReplayStreams({ startTime: start.valueOf().toString(), officeGuid: officeInfo.guid });
        this.loader = true;
        this.preferenceSvc.otherDayPreference.pipe(take(1)).subscribe(
          res => {
            this.loader = false;
            this.preferenceData = res;
            this.preferenceData?.seatDetails?.seatId && this.bookingForm.patchValue({ seat: res.seatDetails });
          },
          err => {
            this.loader = false;
            this.toasts.displayMsg("Failed to fetch preferences.");
          }
        );
      }
    }
    seatInfo && this.seatSvc.changeSeat(seatInfo);
  }

  handleCommuteChange([login, logout, registrationNumber]: [NsEmpWebExpData.ICommuteOption?, NsEmpWebExpData.ICommuteOption?, string?]): void {
    this.bookingForm.get("commute").patchValue({ login, logout, registrationNumber });
  }
  handleMealChange(data: NsEmpWebExpData.IMeal[]): void {
    let mapMeal = new Map<string, NsEmpWebExpData.IMealsEntity>();
    data[0].mealData.map(item => mapMeal.set(item.mealId, item));
    data[0].mealMap = mapMeal;
    this.bookingForm.patchValue({ meal: data });
  }

  saveBooking(): void {
    if (this.formConfigs.mode === "EDIT") {
      const req = editBookingRequest(this.formConfigs.profile, this.bookingForm, this.formConfigs.data);
      this.update.emit(req);
    } else if (this.formConfigs.mode === "CREATE") {
      this.formConfigs.type === "OFFICE" && this.bookingForm.patchValue({ reason: this.bookingReasons?.[0] });
      const req = createBookingRequest(
        this.formConfigs.profile,
        this.bookingForm,
        this.datesDisabled,
        this.dateFormat,
        this.recurrenceBookingEnabled
      );
      if (this.recurrenceBookingEnabled && this.bookingForm.get("recurrence").value.recurrenceVisibility) {
        const ref = this.modals.show(RecurrenceConfirmationComponent, null, { closeOnBackdropClick: false });
        ref.afterClosed.subscribe(res => {
          if (res) {
            this.create.emit([req, true]);
          }
        });
      } else {
        this.create.emit([req, false]);
      }
    }
  }

  setBookingReasons() {
    const typeOfBooking = this.formConfigs.type === "OFFICE" ? "wfoReasonList" : "wfhReasonList";
    return this.apiService.getBookingReasonList(typeOfBooking).subscribe(bookingReason => {
      this.bookingReasons = bookingReason;
    });
  }

  removeAddon(opt: NsEmpWebExpData.IAddon, data?: NsEmpWebExpData.IMealsEntity): void {
    switch (opt.id) {
      case "SEAT":
        this.bookingForm.patchValue({ seat: undefined });
        this.seatSvc.temperarySeat.next(undefined);
        this.seatSvc.changeSeat(undefined);
        this.bookingForm.patchValue({ meal: undefined });
        this.drawerExpanded && this.updateAddon(opt);
        break;
      case "COMMUTE":
        this.bookingForm.get("commute").reset();
        this.commuteSvc.temporaryCommuteSelection = [];
        this.commuteSvc.setAllotedParkingData(null);
        this.drawerExpanded && this.updateAddon(opt);
        break;
      case "MEAL":
        let meal = this.bookingForm.value.meal;
        for (let i = 0; i < meal.length; i++) {
          const checkId = meal[i].mealMap.get(data.mealId);
          if (checkId)
            meal.map(element => {
              const index = element.mealData.findIndex(id => id.mealId === checkId.mealId);
              element.mealMap.delete(checkId.mealId);
              if (index > -1) element.mealData.splice(index, 1);
            });
        }
        if (meal[0].mealData.length === 0) {
          meal = [];
        }
        if (meal.length > 0) this.bookingForm.patchValue({ meal: meal });
        else this.bookingForm.patchValue({ meal: undefined });
        this.mealService.changedMealData(meal);
        break;
    }
  }
}
